import { useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Archive, ChartLine, Copy, Link2, MoreHorizontal, Pause, Pencil, Play, TriangleAlert } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  DescriptionItem,
  DescriptionList,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
  Modal,
  PageHeader,
  type BadgeVariant,
} from "@fox1ck-org/ui";
import { CampaignStatus, type Campaign, type CampaignListItem } from "@gen/tracker/v1/campaign_pb";

import { WriteOnly } from "@/components/auth/write-only";
import { ImportedBadge, LockedNote, lockedBySource } from "@/components/campaign/imported";
import { PhoneActions } from "@/shell/phone";
import { useT, type TKey } from "@/lib/i18n";
import { formatInt, formatMoney } from "@/lib/format";
import { dayIn } from "@/lib/period";
import { formatFilters } from "@/lib/report";
import { narrow } from "@/lib/report-drill";
import { carryPeriod } from "@/lib/route-tab";
import { statsRowToNumbers } from "@/lib/services/reporting-schema-service";
import { useReportTimezone } from "@/lib/services/workspace-service";
import { useGetTrafficSource } from "@/lib/services/traffic-source-service";
import { trafficSourceBrand } from "@/lib/source-types";
import { toastFailure } from "@/lib/plan";
import {
  useActivateCampaign,
  useArchiveCampaign,
  useCampaignCard,
  useDuplicateCampaign,
  usePauseCampaign,
  useRenderAdLink,
} from "@/lib/services/campaign-service";

const STATUS: Record<CampaignStatus, { label: TKey; variant: BadgeVariant }> = {
  [CampaignStatus.UNSPECIFIED]: { label: "campaigns.badge.draft", variant: "neutral" },
  [CampaignStatus.DRAFT]: { label: "campaigns.badge.draft", variant: "neutral" },
  [CampaignStatus.ACTIVE]: { label: "campaigns.badge.active", variant: "ok" },
  [CampaignStatus.PAUSED]: { label: "campaigns.badge.paused", variant: "warn" },
  [CampaignStatus.ARCHIVED]: { label: "campaigns.badge.archived", variant: "neutral" },
};

/** The campaign's status as the card's header draws it: a word with its dot. */
export function CampaignStatusBadge({ status }: { status: CampaignStatus }) {
  const t = useT();
  const s = STATUS[status] ?? STATUS[CampaignStatus.DRAFT];
  return (
    <Badge variant={s.variant} dot>
      {t(s.label)}
    </Badge>
  );
}

/** The address of the campaign in «Отчёты»: the report narrowed to it, the period carried. Pure. */
export function reportHref(campaignId: string, search: string): string {
  const carried = carryPeriod(search);
  const flt = `flt=${encodeURIComponent(formatFilters(narrow({ dims: [], metrics: [] }, "campaign", campaignId)))}`;
  return `/stats${carried ? `${carried}&${flt}` : `?${flt}`}`;
}

/**
 * The card's header (доски CSummary, CActions, CArchive): the name with its
 * status beside it on the title's line, one line of what the campaign is made of — source · offer (its id) ·
 * landers · domain · buyer — and the actions: the link for the source,
 * «Изменить» into the editor, and «⋯» with pause, copy, the link, the
 * report and the archive. The tabs ride under it (`tabs`).
 *
 * On a phone (доска MCampaign) the name is the shell's header and «⋯» sits
 * in it; under the tabs, the status and where the campaign runs.
 */
export function CampaignHeader({
  campaign,
  item,
  tabs,
  onLinks,
  onFlow,
  phone,
}: {
  campaign: Campaign;
  item: CampaignListItem | undefined;
  tabs: ReactNode;
  onLinks: () => void;
  onFlow: () => void;
  phone?: boolean;
}) {
  const t = useT();
  const source = useGetTrafficSource(campaign.trafficSourceId || undefined);
  const sourceLabel = source.data ? trafficSourceBrand(source.data.kind) : (item?.trafficSourceName ?? "");
  if (phone) {
    const where = [item?.countries.join(", "), item?.groupName, sourceLabel, item?.ownerName ? t("card.meta.buyer", { name: item.ownerName }) : ""].filter(Boolean).join(" · ");
    return (
      <div className="flex shrink-0 flex-col gap-2.5 px-3.5 pt-1">
        <PhoneActions>
          <MoreMenu campaign={campaign} item={item} ghost />
        </PhoneActions>
        {tabs}
        <span className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
          <CampaignStatusBadge status={campaign.status} />
          <ImportedBadge campaign={campaign} size="sm" />
          <span className="min-w-0 truncate">{where}</span>
        </span>
        <LockedNote campaign={campaign} />
      </div>
    );
  }
  const offer = item?.offers[0];
  const meta: ReactNode[] = [];
  if (sourceLabel) meta.push(<span key="src">{sourceLabel}</span>);
  if (offer) {
    meta.push(
      <span key="offer" className="flex items-center gap-1.5">
        {t("card.meta.offer")}
        <Link to={`/library/offers/${offer.id}`} className="text-primary hover:underline">
          {offer.name}
        </Link>
        {item && item.offers.length > 1 ? `+${item.offers.length - 1}` : null}
        <span className="font-mono text-xs text-ink-3">#{offer.id.slice(0, 8)}</span>
      </span>,
    );
  }
  if (item && item.landers.length > 0) {
    meta.push(
      <button key="landers" type="button" onClick={onFlow} className="text-primary hover:underline">
        {t("card.meta.landers", { count: item.landers.length })}
      </button>,
    );
  }
  if (item?.fqdn) {
    meta.push(
      <span key="fqdn">
        {t("card.meta.domain")} <span className="font-mono text-xs">{item.fqdn}</span>
      </span>,
    );
  }
  if (item?.ownerName) meta.push(<span key="owner">{t("card.meta.buyer", { name: item.ownerName })}</span>);

  return (
    <PageHeader
      className="shrink-0 px-5 pt-4"
      title={campaign.name}
      titleAddon={
        <>
          <CampaignStatusBadge status={campaign.status} />
          <ImportedBadge campaign={campaign} />
        </>
      }
      subtitle={
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
            {meta.map((m, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 ? (
                  <span aria-hidden className="text-ink-3">
                    ·
                  </span>
                ) : null}
                {m}
              </span>
            ))}
          </span>
          <LockedNote campaign={campaign} />
        </span>
      }
      actions={
        <>
          <Button variant="secondary" onClick={onLinks}>
            <Link2 className="size-4" strokeWidth={1.5} />
            {sourceLabel ? t("card.action.linkFor", { source: sourceLabel }) : t("card.action.link")}
          </Button>
          <WriteOnly>
            {lockedBySource(campaign) ? (
              <Button variant="secondary" disabled>
                <Pencil className="size-4" strokeWidth={1.5} />
                {t("card.action.edit")}
              </Button>
            ) : (
              <Button asChild variant="secondary">
                <Link to={`/campaigns/${campaign.id}/edit`}>
                  <Pencil className="size-4" strokeWidth={1.5} />
                  {t("card.action.edit")}
                </Link>
              </Button>
            )}
          </WriteOnly>
          <MoreMenu campaign={campaign} item={item} />
        </>
      }
      tabs={tabs}
    />
  );
}

/**
 * Pause an active campaign, launch any other one — «⋯»'s first item and the
 * phone card's button beside «Ссылка» (доска MCampaign), one action. An
 * archived campaign has neither.
 */
export function usePauseToggle(campaign: Campaign) {
  const t = useT();
  const pause = usePauseCampaign();
  const activate = useActivateCampaign();
  const active = campaign.status === CampaignStatus.ACTIVE;
  const archived = campaign.status === CampaignStatus.ARCHIVED;
  const locked = lockedBySource(campaign);
  const run = () =>
    (active ? pause : activate).mutate(campaign.id, {
      onSuccess: () => toast.success(t(active ? "campaign.toast.paused" : "campaign.toast.activated", { name: campaign.name })),
      onError: (e: unknown) => toastFailure(e, t("campaign.toast.failed")),
    });
  return { active, archived, locked, pending: pause.isPending || activate.isPending, run };
}

/**
 * «⋯» (доска CActions): pause or resume, a copy, the link, the campaign in
 * «Отчёты», and the archive set apart in red. The letters are the menu's own
 * shortcuts — they act only while it is open, so a stray key on the card
 * never pauses a campaign.
 */
function MoreMenu({ campaign, item, ghost }: { campaign: Campaign; item: CampaignListItem | undefined; ghost?: boolean }) {
  const t = useT();
  const navigate = useNavigate();
  const { search } = useLocation();
  const toggle = usePauseToggle(campaign);
  const duplicate = useDuplicateCampaign();
  const link = useRenderAdLink(campaign.id);
  const [open, setOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const { active, archived, locked } = toggle;
  const fail = (e: unknown) => toastFailure(e, t("campaign.toast.failed"));
  const adLink = link.data?.link?.link ?? "";

  const actions = {
    pause: toggle.run,
    duplicate: () =>
      duplicate.mutate(
        { id: campaign.id },
        {
          onSuccess: (copy) => {
            toast.success(t("card.toast.copied", { name: copy.name }));
            navigate(`/campaigns/${copy.id}/edit`);
          },
          onError: fail,
        },
      ),
    copy: () => {
      if (!adLink) return;
      void navigator.clipboard.writeText(adLink).then(() => toast.success(t("card.toast.linkCopied")));
    },
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    if (locked && (e.key.toLowerCase() === "p" || e.key.toLowerCase() === "d")) return;
    const key = e.key.toLowerCase();
    const run = key === "p" && !archived ? actions.pause : key === "d" ? actions.duplicate : key === "c" && adLink ? actions.copy : undefined;
    if (!run) return;
    e.preventDefault();
    setOpen(false);
    run();
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant={ghost ? "ghost" : "secondary"} size="icon" className={ghost ? "size-11" : undefined} aria-label={t("campaign.action.more")}>
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64" onKeyDown={onKey}>
          <WriteOnly>
            {!archived ? (
              <DropdownMenuItem disabled={toggle.pending || locked} onSelect={actions.pause}>
                {active ? <Pause className="size-4" /> : <Play className="size-4" />}
                {active ? t("card.menu.pause") : t("campaign.action.activate")}
                <DropdownMenuShortcut>P</DropdownMenuShortcut>
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem disabled={duplicate.isPending || locked} onSelect={actions.duplicate}>
              <Copy className="size-4" /> {t("campaign.action.duplicate")}
              <DropdownMenuShortcut>D</DropdownMenuShortcut>
            </DropdownMenuItem>
          </WriteOnly>
          <DropdownMenuItem disabled={!adLink} onSelect={actions.copy}>
            <Link2 className="size-4" /> {t("card.menu.copyLink")}
            <DropdownMenuShortcut>C</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to={reportHref(campaign.id, search)}>
              <ChartLine className="size-4" /> {t("card.menu.report")}
            </Link>
          </DropdownMenuItem>
          {!archived ? (
            <WriteOnly>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" disabled={locked} onSelect={() => setArchiveOpen(true)}>
                <Archive className="size-4" /> {t("campaign.action.archive")}
              </DropdownMenuItem>
            </WriteOnly>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
      {archiveOpen ? <ArchiveDialog campaign={campaign} item={item} onClose={() => setArchiveOpen(false)} /> : null}
    </>
  );
}

// The dialog's pairs as on the board: the label a fixed column, the value
// starting right after it and wrapping — not the package's justified,
// truncated row («кампанию можно вернуть из…» was cut).
const ARCHIVE_PAIR = "grid grid-cols-[5.5rem_minmax(0,1fr)] justify-start gap-3 [&>dd]:whitespace-normal";

/**
 * «Архивировать кампанию?» (доска CArchive): what stops — the link no longer
 * opens the campaign while the ads keep spending in the cabinet — and what
 * goes on: today's numbers, the leads in hold whose postbacks are still taken,
 * the statistics that stay. Today's figures are the list's row for today, from
 * the server.
 */
function ArchiveDialog({ campaign, item, onClose }: { campaign: Campaign; item: CampaignListItem | undefined; onClose: () => void }) {
  const t = useT();
  const navigate = useNavigate();
  const tz = useReportTimezone();
  const archive = useArchiveCampaign();
  const today = dayIn(new Date(), tz);
  const card = useCampaignCard(campaign.id, { from: today, to: today, tz }, undefined);
  const v = card.data?.stats ? statsRowToNumbers(card.data.stats) : undefined;
  const hold = item?.stats ? statsRowToNumbers(item.stats).conversions_hold : undefined;

  return (
    <Modal
      open
      onClose={() => (archive.isPending ? undefined : onClose())}
      title={t("card.archive.title")}
      description={campaign.name}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={archive.isPending}>
            {t("card.archive.cancel")}
          </Button>
          <Button
            variant="danger"
            disabled={archive.isPending}
            onClick={() =>
              archive.mutate(campaign.id, {
                onSuccess: () => {
                  toast.success(t("campaign.archive.done", { name: campaign.name }));
                  navigate("/campaigns");
                },
                onError: (e) => toastFailure(e, t("campaign.toast.failed")),
              })
            }
          >
            <Archive className="size-4" /> {t("card.archive.confirm")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Alert variant="warn">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          <div className="flex min-w-0 flex-col gap-1">
            <AlertTitle>{t("card.archive.stops")}</AlertTitle>
            <AlertDescription>{t("card.archive.stops.body", { source: item?.trafficSourceName || t("links.source") })}</AlertDescription>
          </div>
        </Alert>
        <DescriptionList>
          <DescriptionItem
            className={ARCHIVE_PAIR}
            label={t("card.archive.today")}
            value={card.isLoading ? "…" : t("card.archive.today.value", { clicks: formatInt(v?.clicks), leads: formatInt(v?.leads), cost: formatMoney(v?.cost) })}
          />
          <DescriptionItem className={ARCHIVE_PAIR} label={t("kpi.hold")} value={t("card.archive.hold", { n: formatInt(hold) })} />
          <DescriptionItem className={ARCHIVE_PAIR} label={t("card.archive.stats")} value={t("card.archive.stats.value")} />
        </DescriptionList>
      </div>
    </Modal>
  );
}
